import axios from 'axios';
import { AgTechHttpRequestProps, AgTechHttpResponse, useAgTechHttpRequestConfigurationBuilder, AgTechApiEntityResponse, AgTechHttpResponses } from '../../data/actions/AgTechHttpRequests';
import { AgTechApiResponseHandlers, executeAgTechApiRequest, handleSuccessfulApiResponse, AgTechInternalAxiosRequestProps } from '../../data/http/AgTechCoreApiRequests';
import { logEvent } from '../../logging/AgTechLogger';

export type AgTechApiPostRequestProps<TPostedData, TResponseData> = AgTechHttpRequestProps & AgTechApiResponseHandlers<TResponseData> & {
    postedData: TPostedData
}

export type AgTechPostRequest<TInput, TResponseData> = {
    post: (props: AgTechApiPostRequestProps<TInput, TResponseData>) => Promise<AgTechHttpResponse<TResponseData>>
}

export const useAgTechHttpPostRequest = <TPostedData, TResponseData>(): AgTechPostRequest<TPostedData, TResponseData> => {
    let requestConfigurationBuilder = useAgTechHttpRequestConfigurationBuilder();

    return {
        post: async requestProps => await executeAgTechApiRequest<TPostedData, TResponseData>({
            requestType: 'POST',
            requestData: requestProps.postedData,
            requestConfiguration: requestConfigurationBuilder(requestProps),
            requestResponseHandlers: requestProps,
            request: executePostRequestInternal
        })
    }
}

export const useAgTechHttpPostEntityRequest = <TPostedEntity>(): AgTechPostRequest<TPostedEntity, TPostedEntity> => {
    let httpPostAndConvertRequest = useAgTechHttpPostAndConvertRequest<TPostedEntity, AgTechApiEntityResponse<TPostedEntity>, TPostedEntity>();

    return {
        post: async requestProps => httpPostAndConvertRequest.post({
            ...requestProps,
            convert: entityResponse => entityResponse.data
        })
    }
}

export const useAgTechHttpSubmitEntityRequest = <TPostedEntity>(): AgTechPostRequest<TPostedEntity, TPostedEntity> => {
    let httpPostAndConvertRequest = useAgTechHttpPostAndConvertRequest<TPostedEntity, AgTechApiEntityResponse<TPostedEntity>, TPostedEntity>();

    return {
        post: async requestProps => httpPostAndConvertRequest.post({
            ...requestProps,
            path: requestProps.path + '/Submit',
            convert: entityResponse => entityResponse.data
        })
    }
}

export const useAgTechHttpSubmitEntityAndConvertRequest = <TPostedEntity, TConvertedEntity>(): AgTechPostAndReturnRequest<TPostedEntity, TPostedEntity, TConvertedEntity> => {
    let httpPostAndConvertRequest = useAgTechHttpPostAndConvertRequest<TPostedEntity, AgTechApiEntityResponse<TPostedEntity>, TConvertedEntity>();

    return {
        post: async requestProps => httpPostAndConvertRequest.post({
            ...requestProps,
            path: requestProps.path + '/Submit',
            convert: entityResponse => requestProps.convert(entityResponse.data)
        })
    }
}

///
/// Conversions
///

export type AgTechPostAndReturnRequestProps<TPostedData, TResponseData, TResult> = AgTechApiPostRequestProps<TPostedData, TResult> & {
    convert: (responseData: TResponseData) => TResult
}

export type AgTechPostAndReturnRequest<TPostedData, TResponseData, TResult> = {
    post: (props: AgTechPostAndReturnRequestProps<TPostedData, TResponseData, TResult>) => Promise<AgTechHttpResponse<TResult>>
}

export const useAgTechHttpPostAndConvertRequest = <TPostedData, TResponseData, TResult>(): AgTechPostAndReturnRequest<TPostedData, TResponseData, TResult> => {
    let requestConfigurationBuilder = useAgTechHttpRequestConfigurationBuilder();

    return {
        post: async props => {
            let requestResponse = AgTechHttpResponses.Failed('An error occurred');
            let requestConfiguration = requestConfigurationBuilder(props);

            let initialRequestProps: AgTechApiPostRequestProps<TPostedData, TResponseData> = {
                path: props.path,
                postedData: props.postedData,
                onSuccess: response => AgTechHttpResponses.Success(response.body),
                onError: failure => AgTechHttpResponses.Failed(failure.error)
            };

            let initialDataPostResponse = await executeAgTechApiRequest<TPostedData, TResponseData>({
                requestType: 'POST',
                requestData: initialRequestProps.postedData,
                requestConfiguration: requestConfiguration,
                requestResponseHandlers: initialRequestProps,
                request: executePostRequestInternal
            });

            if (initialDataPostResponse.success) {
                let initialData = initialDataPostResponse.success.data;
                let convertedData = props.convert(initialData);

                requestResponse = await handleSuccessfulApiResponse({
                    ...requestConfiguration,
                    response: { wasSuccessful: true, data: convertedData },
                    handlers: props
                });
            }
            else {
                // Carry the real failure (errorMessage/errorCode from onError above) through instead
                // of the generic placeholder this was initialized with - callers branching on
                // errorCode (e.g. a concurrency conflict) need the actual response, not a stand-in.
                requestResponse = {
                    wasAttempted: true,
                    errorMessage: initialDataPostResponse.errorMessage,
                    errorCode: initialDataPostResponse.errorCode
                };
            }

            return requestResponse;
        }
    }
}


/// Internal

const executePostRequestInternal = <TInputData, TData>(props: AgTechInternalAxiosRequestProps<TInputData>) => {
    return axios.post<TData>(props.requestURL, props.requestData, props.requestConfig);
}